import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, MantineProvider } from '@mantine/core';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';

// Dashboard Comercial Layout & Pages
import DCLayout from './components/DashboardComercial/DCLayout';
import Dashboard from './components/DashboardComercial/Dashboard';
import CapTd from './components/DashboardComercial/CapTd';
import TdIti from './components/DashboardComercial/TdIti';
import Foc from './components/DashboardComercial/Foc';
import CargaArchivos from './components/DashboardComercial/CargaArchivos';
import Comercial from './components/DashboardComercial/Comercial';
import DashboardV2 from './components/DashboardComercial/DashboardV2';
import GlobalLoaderProvider from './components/DashboardComercial/GlobalLoader';
import Login from './components/Login/page';

// SSCN Front (ProtectedRoutes)
import ProtectedRoute from './components/ProtectedRoutes/page';
import Home from './components/Home/page';
import Conciliaciones from './components/Conciliaciones/page';
import Reportes from './components/Reportes/page';
import ReportesRDI from './components/ReportesRDI/page';
import CifrasRDI from './components/CifrasRDI/page';
import ErrorPage from './components/ErrorPage/page';

import AppContextProvider from './context/context';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import './App.css';

const theme = createTheme({});

// Correos autorizados
const AUTHORIZED_EMAILS = [
  'lforozco@aeromexico.com',
  'angonzalezg@aeromexico.com',
  'lgarciaav@aeromexico.com',
  'rvara@aeromexico.com',
  'rperezca@aeromexico.com',
  'jiojeda@aeromexico.com',
  'eotera@aeromexico.com',
  'epalomaresp@aeromexico.com',
  'asilvad@aeromexico.com',
  'fisidoro@aeromexico.com',
  'igarciasi@aeromexico.com',
  'ecruzsa@aeromexico.com',
  'mcardenasy@aeromexico.com',
  'jmorenod@aeromexico.com',
  'asorianor@aeromexico.com',
  'ogarfias@aeromexico.com',
  'rtovar@aeromexico.com',
  'dalemana@aeromexico.com',
  //'ilhernandezr@aeromexico.com'
];

function App() {
  const { isAuthenticated, isLoading, user, error, getAccessTokenSilently } = useAuth0();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!isLoading) { setReady(true); return; }
    const timeout = setTimeout(() => setReady(true), 3000);
    return () => clearTimeout(timeout);
  }, [isLoading]);

  // Obtener y guardar el token en sessionStorage tan pronto como el usuario esté
  // autenticado, sin importar a qué ruta entró. Así todas las peticiones del DC
  // (y cualquier otra parte del app) tienen el token disponible de inmediato.
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        sessionStorage.setItem('token', token);
        if (user?.nickname) sessionStorage.setItem('user', user.nickname);
      } catch (e) {
        console.error('[AUTH] No se pudo obtener el token:', e.message);
      }
    };
    fetchToken();
  }, [isAuthenticated, getAccessTokenSilently, user]);

  if (!ready) return null;

  if (error) console.warn('[AUTH] Error Auth0:', error.message);

  if (!isAuthenticated) {
    return <MantineProvider theme={theme}><Login /></MantineProvider>;
  }

  const userEmail = (user?.email || '').toLowerCase();
  const isAuthorized = AUTHORIZED_EMAILS.some((e) => e.toLowerCase() === userEmail);

  if (!isAuthorized) {
    return (
      <MantineProvider theme={theme} withCSSVariables>
        <Notifications position="top-right" zIndex={999} />
        <AppContextProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Navigate to="/Home" replace />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/Home" element={<Home />} />
                <Route path="/Conciliaciones" element={<Conciliaciones />} />
                <Route path="/Reportes" element={<Reportes />} />
                <Route path="/ReportesRDI" element={<ReportesRDI />} />
                <Route path="/CifrasRDI" element={<CifrasRDI />} />
              </Route>
              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </BrowserRouter>
        </AppContextProvider>
      </MantineProvider>
    );
  }

  return (
    <MantineProvider theme={theme} withCSSVariables>
      <Notifications position="top-right" zIndex={999} />
      <AppContextProvider>
        <GlobalLoaderProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Navigate to="/dc" replace />} />
              <Route path="/Home" element={<Navigate to="/dc" replace />} />
              <Route element={<DCLayout />}>
                <Route path="/dc" element={<DashboardV2 />} />
                <Route path="/dc/carga" element={<CargaArchivos />} />
                <Route path="/dc/cap-td" element={<CapTd />} />
                <Route path="/dc/td-iti" element={<TdIti />} />
                <Route path="/dc/fco" element={<Foc />} />
                <Route path="/dc/comercial" element={<Comercial />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </GlobalLoaderProvider>
      </AppContextProvider>
    </MantineProvider>
  );
}

export default App;
